import { useEffect, useState, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Paper,
  ClickAwayListener,
  Divider,
  Popper,
} from "@mui/material";

import mainStyle from "./mainStyle.module.scss";

import { ReactComponent as ChevronUp } from "assets/images/icons/icn_pagination_chevron_up.svg";
import { ReactComponent as ChevronUpHover } from "assets/images/icons/icn_pagination_chevron_up_hover.svg";
import { ReactComponent as ChevronDown } from "assets/images/icons/icn_pagination_chevron_down.svg";
import { ReactComponent as ChevronDownHover } from "assets/images/icons/icn_pagination_chevron_down_hover.svg";

export const MenuSelect = ({
  value,
  onChange,
  options = [],
  pinningOptions,
  width,
  placeholder,
  disabled,
  borderLeftWidth,
  borderRightWidth,
  borderBottomWidth,
  isEditedField = false,
  additionZIndex = 0,
  placement = "bottom-end",
  startIcon = null,
  renderOption,
  renderValue,
}) => {
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState(null);
  const [chevronHover, setChevronHover] = useState(false);
  const [menuWidthPx, setMenuWidthPx] = useState(width);

  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  const menuOpen = Boolean(anchorEl);

  const handleOpen = (event) => {
    if (disabled) {
      return;
    }

    event.stopPropagation();

    triggerRef.current = event.currentTarget;

    setAnchorEl(anchorEl ? null : event.currentTarget);

    if (containerRef.current) {
      setMenuWidthPx(containerRef.current.offsetWidth);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setChevronHover(false);

    if (triggerRef.current) {
      triggerRef.current.focus();
    }
  };

  useEffect(() => {
    if (containerRef.current) {
      setMenuWidthPx(containerRef.current.offsetWidth);
    }
  }, [width]);

  const allOptions = [
    ...options,
    ...(pinningOptions || []),
  ];

  const selectedOption = allOptions.find(
    (opt) => String(opt.value) === String(value),
  );

  const label = selectedOption?.label || "";

  return (
    <Box
      ref={containerRef}
      onClick={handleOpen}
      onMouseEnter={() => {
        if (!disabled) {
          setChevronHover(true);
        }
      }}
      onMouseLeave={() => {
        if (!disabled) {
          setChevronHover(false);
        }
      }}
      sx={{
        position: "relative",
        display: "flex",

        border: isEditedField
          ? `2px solid ${theme.palette.primary.orange}`
          : `1px solid ${
              disabled
                ? theme.palette.primary.borderGreyPrimary
                : menuOpen
                  ? theme.palette.primary.main
                  : chevronHover
                    ? theme.palette.primary.mainHover
                    : theme.palette.primary.greyFifth
            }`,

        borderRadius:
          theme.shape.customBorderRadius[1],

        width,
        height: 29,

        bgcolor: theme.palette.primary.white,

        pl: 1,
        pr: "32px",

        cursor: disabled
          ? "not-allowed"
          : "pointer",

        opacity: disabled ? 0.6 : 1,
      }}
      tabIndex={0}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          overflow: "hidden",
          flexGrow: 1,
          minWidth: 0,
        }}
      >
        {startIcon && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mr: 0.5,
              flexShrink: 0,
            }}
          >
            {startIcon}
          </Box>
        )}

        {selectedOption ? (
          renderValue ? (
            <Box
              sx={{
                width: "100%",
                minWidth: 0,
                display: "flex",
                alignItems: "center",
              }}
            >
              {renderValue(selectedOption)}
            </Box>
          ) : (
            <Typography
              sx={{
                fontSize:
                  theme.typography.customFontSize[2],

                color:
                  theme.palette.primary.greyPrimary,

                fontWeight:
                  theme.typography.customFontWeight[3],

                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontFamily: "Open Sans",
              }}
            >
              {label}
            </Typography>
          )
        ) : (
          <Typography
            sx={{
              fontSize:
                theme.typography.customFontSize[2],

              color:
                theme.palette.primary.greyThird,

              fontWeight:
                theme.typography.customFontWeight[3],

              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontFamily: "Open Sans",
            }}
          >
            {placeholder}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,

          width: 32,
          height: "100%",

          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          borderTopRightRadius: 6,
          borderBottomRightRadius: 6,

          pointerEvents: "auto",
        }}
      >
        {menuOpen ? (
          chevronHover ? (
            <ChevronUpHover />
          ) : (
            <ChevronUp />
          )
        ) : chevronHover ? (
          <ChevronDownHover />
        ) : (
          <ChevronDown />
        )}
      </Box>

      {menuOpen && (
        <Popper
          open={menuOpen}
          anchorEl={anchorEl}
          placement={placement}
          disablePortal={false}
          style={{
            zIndex: theme.zIndex.modal,
            width: menuWidthPx,
          }}
          modifiers={[
            {
              name: "offset",
              options: {
                offset: [0, 8],
              },
            },
          ]}
        >
          <ClickAwayListener
            onClickAway={handleClose}
          >
            <Paper
              elevation={3}
              sx={{
                width: menuWidthPx,

                borderRadius: 3,

                boxShadow:
                  "0px 1px 4px rgba(0,0,0,0.08)",

                zIndex:
                  theme.zIndex.modal +
                  additionZIndex,

                "&::before": {
                  content: '""',

                  position: "absolute",

                  top: -8,
                  right: 16,

                  width: 0,
                  height: 0,

                  borderLeft: `${borderLeftWidth} solid transparent`,
                  borderRight: `${borderRightWidth} solid transparent`,
                  borderBottom: `${borderBottomWidth} solid #ffffff`,

                  filter:
                    "drop-shadow(-2px -1px 2px rgba(0, 0, 0, 0.05)) drop-shadow(2px -1px 2px rgba(0, 0, 0, 0.05))",
                },
              }}
            >
              {/* ========================================
                  Normal Options
              ======================================== */}
              <List
                className={
                  mainStyle[
                    "custom-scrollbar-menu"
                  ]
                }
                sx={{
                  paddingTop: "5px",
                  paddingLeft: "10px",
                  paddingRight: "10px",

                  boxSizing: "content-box",

                  marginRight: "10px",

                  maxHeight: "180px",

                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                {options.map((opt) => (
                  <ListItem
                    key={opt.value}
                    button
                    disabled={opt.disabled}
                    selected={
                      String(value) ===
                      String(opt.value)
                    }
                    onClick={(e) => {
                      e.stopPropagation();

                      if (opt.disabled) {
                        return;
                      }

                      onChange(opt);

                      handleClose();
                    }}
                    sx={{
                      color:
                        theme.palette.primary.main,

                      paddingLeft: "10px",

                      margin: "5px 5px 0 0",

                      borderRadius:
                        theme.shape
                          .customBorderRadius[0],

                      height: "40px",

                      "&:hover": {
                        backgroundColor:
                          theme.palette.primary
                            .lightBlue,
                      },

                      "&.Mui-selected": {
                        backgroundColor:
                          theme.palette.primary
                            .subBlue,
                      },

                      "&.Mui-selected:hover": {
                        backgroundColor:
                          theme.palette.primary
                            .subBlue,
                      },
                    }}
                  >
                    {/* ============================
                        有 renderOption
                        → 使用外部自訂內容

                        沒有 renderOption
                        → 維持原本 ListItemText
                    ============================ */}
                    {renderOption ? (
                      renderOption(opt)
                    ) : (
                      <ListItemText
                        primary={opt.label}
                        primaryTypographyProps={{
                          style: {
                            textOverflow:
                              "ellipsis",

                            overflow: "hidden",

                            whiteSpace:
                              "nowrap",

                            fontSize:
                              theme.typography
                                .customFontSize[2],
                          },
                        }}
                      />
                    )}
                  </ListItem>
                ))}
              </List>

              {/* ========================================
                  Pinning Options
              ======================================== */}
              {pinningOptions && (
                <Box
                  sx={{
                    px: "10px",
                    pb: "5px",
                  }}
                >
                  <Divider />

                  <List
                    sx={{
                      p: 0,
                    }}
                  >
                    {pinningOptions.map(
                      (opt) => (
                        <ListItem
                          key={opt.value}
                          button
                          disabled={
                            opt.disabled
                          }
                          selected={
                            String(value) ===
                            String(opt.value)
                          }
                          onClick={(e) => {
                            e.stopPropagation();

                            if (
                              opt.disabled
                            ) {
                              return;
                            }

                            onChange(opt);

                            handleClose();
                          }}
                          sx={{
                            color:
                              theme.palette
                                .primary.main,

                            paddingLeft:
                              "10px",

                            mt: "5px",

                            borderRadius:
                              theme.shape
                                .customBorderRadius[0],

                            height: "40px",

                            display: "flex",

                            justifyContent:
                              "space-between",

                            alignItems:
                              "center",

                            "&:hover": {
                              backgroundColor:
                                theme.palette
                                  .primary
                                  .lightBlue,
                            },

                            "&.Mui-selected": {
                              backgroundColor:
                                theme.palette
                                  .primary
                                  .subBlue,
                            },

                            "&.Mui-selected:hover":
                              {
                                backgroundColor:
                                  theme.palette
                                    .primary
                                    .subBlue,
                              },
                          }}
                        >
                          {renderOption ? (
                            renderOption(opt)
                          ) : (
                            <>
                              <ListItemText
                                primary={
                                  opt.label ||
                                  opt.value
                                }
                                primaryTypographyProps={{
                                  style: {
                                    textOverflow:
                                      "ellipsis",

                                    overflow:
                                      "hidden",

                                    whiteSpace:
                                      "nowrap",

                                    fontSize:
                                      theme
                                        .typography
                                        .customFontSize[2],
                                  },
                                }}
                              />

                              {opt.showArrow && (
                                <Box
                                  sx={{
                                    display:
                                      "flex",

                                    alignItems:
                                      "center",

                                    pl: 1,

                                    color:
                                      theme
                                        .palette
                                        .primary
                                        .greySecondary ||
                                      "grey",
                                  }}
                                >
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <line
                                      x1="5"
                                      y1="12"
                                      x2="19"
                                      y2="12"
                                    />

                                    <polyline
                                      points="
                                        12 5
                                        19 12
                                        12 19
                                      "
                                    />
                                  </svg>
                                </Box>
                              )}
                            </>
                          )}
                        </ListItem>
                      ),
                    )}
                  </List>
                </Box>
              )}
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </Box>
  );
};